import { computed, nextTick, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import type { AnyVariables, OperationResult } from '@urql/vue'

import type { LoginMutation, LoginWithGoogleMutation } from '@/graphql/api'
import { useLoginMutation, useLoginWithGoogleMutation } from '@/graphql/api'
import { useTranslator } from '@/i18n'
import { assertNever } from '@/helpers'
import { getEmail } from '@/types/email'
import { useAuth } from '@/modules/auth'
import { useSegment } from '@/composables/app/gtm/useSegment'
import { useCustomerRegistrationInputStore } from '../useCustomerRegistrationInput'
import { useIsAdmin } from '@/components/app/isAdmin'

export default function useLogin() {
    const { t } = useI18n()

    const { setAuth } = useAuth()

    const {
        executeMutation: executeLogin,
        error: errorLogin,
        fetching: fetchingLogin,
    } = useLoginMutation()
    const {
        executeMutation: executeLoginWithGoogle,
        error: errorLoginWithGoogle,
        fetching: fetchingLoginWithGoogle,
    } = useLoginWithGoogleMutation()

    const fetching = computed(
        () => fetchingLogin.value || fetchingLoginWithGoogle.value
    )

    const { locale } = useTranslator()

    const failureError = ref('')
    const errorMessage = computed(
        () =>
            errorLogin?.value?.message ||
            errorLoginWithGoogle?.value?.message ||
            failureError.value
    )
    const emailError = ref('')

    const identifier = ref('')
    const password = ref('')

    const { login: trackLogin } = useSegment()

    const isAdmin = useIsAdmin()

    const handleLoginResponse = (
        result: OperationResult<
            LoginMutation | LoginWithGoogleMutation,
            AnyVariables
        >
    ) => {
        const data =
            result.data && 'login' in result.data
                ? result.data?.login
                : result.data?.loginWithGoogle

        if (!data) {
            return undefined
        }

        switch (data.__typename) {
            case 'LoginResponseSuccess':
                trackLogin(data.user)

                nextTick(() => {
                    setAuth(data)
                })
                break
            case 'LoginResponseFailure':
                failureError.value = data.message
                break
            default:
                assertNever(data, data)
                break
        }

        return data
    }

    const login = () => {
        failureError.value = ''

        const email = getEmail(identifier.value)

        if (!email) {
            emailError.value = t('globals.invalid_email_error')

            return
        } else {
            emailError.value = ''
        }

        return executeLogin({
            credentials: {
                identifier: email,
                password: password.value,
            },
            locale: locale.value,
            isConsultant: isAdmin.value,
        }).then(handleLoginResponse)
    }

    const { customerRegistrationInput } = storeToRefs(
        useCustomerRegistrationInputStore()
    )

    const loginWithGoogle = () => {
        if (customerRegistrationInput.value?.byGoogle) {
            return executeLoginWithGoogle({
                credentials: customerRegistrationInput.value.byGoogle,
                locale: locale.value,
                isConsultant: isAdmin.value,
            }).then(handleLoginResponse)
        }

        return Promise.reject(new Error('No Google credentials'))
    }

    return {
        identifier,
        password,
        login,
        loginWithGoogle,
        fetching,
        errorMessage,
        emailError,
    }
}
